/**
 * useUnifiedSearch — THE single search pipeline for all listing surfaces.
 *
 * Every page that displays searchable items (Home, Shop, LadiesHub, CropHome,
 * StudentHub) MUST use this hook instead of rolling its own filter/dedupe logic.
 *
 * Pipeline: items → memoized index → filter by query → deduplicate → return
 *
 * Normalization rules (applied to both stored text AND query):
 *  - trim + collapse spaces
 *  - lowercase English
 *  - Arabic: أ/إ/آ→ا, ى→ي, ة→ه, remove tatweel, strip diacritics
 *  - strip non-alphanumeric (keep Arabic + Latin + digits)
 *
 * Matching: every query token (after Arabic prefix expansion) must appear
 * as a substring OR exact token in the item's indexed text across all
 * searchable fields (title, description, category, tags, location, etc.).
 *
 * Deduplication: by ad_id || id || book_id || opp_id (first occurrence wins).
 */

import { useMemo, useRef } from "react";
import {
  attachSearchIndex,
  filterWithUnifiedSearch,
  SEARCH_FIELDS_COMMON,
} from "../utils/unifiedSearch";
import { dedupeAdsById } from "../utils/dedupeAds";

/**
 * @param {Array}  items       - raw items from API / state
 * @param {string} query       - current search query (may be empty)
 * @param {Object} [options]
 * @param {string[]} [options.fields]  - override search fields
 * @param {string}   [options.idKey]   - override dedup ID field (default: ad_id|id)
 * @returns {Array} deduplicated, filtered items — ready to render
 */
export function useUnifiedSearch(items, query, options = {}) {
  const { fields = SEARCH_FIELDS_COMMON, idKey } = options;

  // Stable reference for fields array to avoid re-indexing on every render
  const fieldsRef = useRef(fields);
  if (
    fields.length !== fieldsRef.current.length ||
    fields.some((f, i) => f !== fieldsRef.current[i])
  ) {
    fieldsRef.current = fields;
  }
  const stableFields = fieldsRef.current;

  // Step 1: Attach search index (memoized — only recomputes when items change)
  const indexedItems = useMemo(
    () => attachSearchIndex(items || [], stableFields),
    [items, stableFields]
  );

  // Step 2: Filter by query (memoized — only recomputes when query or items change)
  const filtered = useMemo(() => {
    if (!query || !String(query).trim()) return indexedItems;
    return filterWithUnifiedSearch(indexedItems, query, stableFields);
  }, [indexedItems, query, stableFields]);

  // Step 3: Deduplicate (always, even without a query — prevents section overlap)
  const deduped = useMemo(
    () => dedupeAdsById(filtered, idKey),
    [filtered, idKey]
  );

  return deduped;
}

export default useUnifiedSearch;
